import type { AdventureManifest } from "../types/adventure.js";

/**
 * Adventure listing (Z04).
 *
 * Static HTTP can't enumerate directories, so the engine reads
 * `/engine/adventures.json` — a hand-authored registry that ships one
 * entry per adventure ID — and then fetches each adventure's
 * `manifest.json`. The result is everything the picker needs to render
 * a selection row (title, author, version, and a base URL to hand to
 * `mountAdventure`).
 *
 * A broken or missing manifest is logged and skipped; one bad adventure
 * never prevents the others from being pickable.
 */

export interface AdventureListingEntry {
  readonly id: string;
  readonly baseUrl: string;
  readonly manifest: AdventureManifest;
}

export interface AdventureIndex {
  readonly adventures: readonly string[];
}

export interface ListAdventuresDeps {
  /** URL of the index file. Defaults to `/engine/adventures.json`. */
  readonly indexUrl?: string;
  /** Fetch implementation; injected for tests. Defaults to global `fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Logger for manifest errors. Defaults to `console.warn`. */
  readonly onError?: (id: string, error: unknown) => void;
}

/**
 * Resolve every adventure advertised in `/engine/adventures.json`.
 * Adventures whose manifest fails to load are dropped from the result
 * (after calling `onError`) so the picker keeps working.
 */
export async function listAdventures(
  deps: ListAdventuresDeps = {},
): Promise<readonly AdventureListingEntry[]> {
  const indexUrl = deps.indexUrl ?? "/engine/adventures.json";
  const doFetch = deps.fetchImpl ?? fetch;
  const onError = deps.onError ?? ((id, e) => console.warn(`[adventure ${id}]`, e));

  const indexRes = await doFetch(indexUrl);
  if (!indexRes.ok) {
    throw new Error(`Failed to load adventure index: HTTP ${String(indexRes.status)}`);
  }
  const index = (await indexRes.json()) as AdventureIndex;
  const ids = Array.isArray(index.adventures) ? index.adventures : [];

  const settled = await Promise.all(
    ids.map(async (id): Promise<AdventureListingEntry | null> => {
      const baseUrl = `/${id}`;
      try {
        const res = await doFetch(`${baseUrl}/manifest.json`);
        if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
        const manifest = (await res.json()) as AdventureManifest;
        return { id, baseUrl, manifest };
      } catch (e) {
        onError(id, e);
        return null;
      }
    }),
  );

  return settled.filter((x): x is AdventureListingEntry => x !== null);
}
